/**
 * Deck overlay service.
 *
 * Standard path: inject layer creates Deck in render(); design GLB added via
 * resolveLayers for flicker-free rendering with correct occlusion and transparency.
 * Legacy path (?debug_overlay=mapbox-layer|legacy): design uses MapboxLayer directly.
 */
import type { MapboxMap } from "@/models";
import type { Layer } from "@deck.gl/core";

type DeckLayer = Layer<any>;
import { designGlbLayerId, designGlbDebugLayerId } from "@/services/designGlb.service";

const INJECT_LAYER_ID = "coup-debug-inject";
const injectDeckLayerIds = new Set<string>();

/** Layer ids we add ourselves (design, debug); not from MapboxLayer with type */
const INJECT_OWN_LAYER_IDS = new Set([designGlbLayerId, designGlbDebugLayerId]);

export function isInjectMode(map: MapboxMap | null): boolean {
  return !!(map && map.getLayer(INJECT_LAYER_ID));
}

let _lastMapboxLayerKeys = "";

/**
 * Sync mapboxLayers into the inject deck's layers. The inject deck has isExternal=true
 * so deck-utils updateLayers no-ops; we must manually merge so ABM trips/heatmap etc. render.
 * Runs every frame so animated props (e.g. TripsLayer currentTime) stay in sync.
 */
export function syncInjectDeckMapboxLayers(map: MapboxMap): void {
  const deck = (map as { __deck?: unknown }).__deck as
    | {
        props: { layers?: DeckLayer[] };
        setProps: (p: { layers: DeckLayer[] }) => void;
        needsRedraw?: (opts?: { clearRedrawFlags?: boolean }) => void;
        userData: { mapboxLayers?: Set<{ id: string; props: { type?: unknown } }> };
      }
    | undefined;
  if (!deck?.userData?.mapboxLayers) return;

  const ourLayers = (deck.props.layers ?? []).filter((l) =>
    INJECT_OWN_LAYER_IDS.has(l.id)
  );
  const mapboxDerived: DeckLayer[] = [];
  for (const ml of deck.userData.mapboxLayers) {
    if (INJECT_OWN_LAYER_IDS.has(ml.id)) continue;
    const LayerType = ml.props?.type;
    if (!LayerType || typeof LayerType !== "function") continue;
    // Shallow copy props so deck gets fresh refs; avoids stale cached data after layer replace
    const props = { ...ml.props };
    mapboxDerived.push(new (LayerType as new (p: unknown) => DeckLayer)(props));
  }

  const keys = Array.from(deck.userData.mapboxLayers)
    .map((m) => m.id)
    .sort()
    .join(",");
  const layerSetChanged = keys !== _lastMapboxLayerKeys;
  _lastMapboxLayerKeys = keys;

  deck.setProps({ layers: [...ourLayers, ...mapboxDerived] });

  if (layerSetChanged) {
    deck.needsRedraw?.({ clearRedrawFlags: true });
    (map as { triggerRepaint?: () => void }).triggerRepaint?.();
  }
}

/**
 * Add design layer to the inject Deck using resolveLayers. Adds a MapboxLayer
 * at beforeId: 'groundfloor' so the design renders before buildings for correct
 * depth occlusion and semi-transparent building blending.
 */
export function addDesignLayerToInjectDeck(
  map: MapboxMap,
  designLayer: DeckLayer
): boolean {
  if (!map.getLayer(INJECT_LAYER_ID)) return false;

  const deck = (map as { __deck?: unknown }).__deck;
  if (!deck) {
    map.once("render", () => addDesignLayerToInjectDeck(map, designLayer));
    return true;
  }

  const { resolveLayers } = require("@deck.gl/mapbox/dist/es5/resolve-layers");

  // 1. Add layer to deck so drawLayer can find it
  const d = deck as {
    props: { layers?: DeckLayer[] };
    setProps: (p: { layers: DeckLayer[] }) => void;
    userData: { mapboxLayers: Set<unknown> };
  };
  const current = d.props.layers ?? [];
  d.setProps({ layers: [...current, designLayer] });

  // 2. Add MapboxLayer to map via resolveLayers – draws during render at beforeId
  resolveLayers(map, deck, [], [designLayer]);

  injectDeckLayerIds.add(designLayer.id);
  if (process.env.NODE_ENV === "development") {
    console.log(
      "[deckOverlay] Design layer added to inject Deck via resolveLayers:",
      designLayer.id
    );
  }
  return true;
}

export function addDeckLayerToOverlay(_layer: unknown, _map: MapboxMap): void {
  // no-op – MapboxOverlay not used
}

export function removeDeckLayerFromOverlay(layerId: string): void {
  injectDeckLayerIds.delete(layerId);
}

export function updateDeckLayerInOverlay(_layer: unknown): void {
  // no-op
}

export function getOverlayLayerIds(): string[] {
  return Array.from(injectDeckLayerIds);
}

export function hasOverlayLayer(layerId: string): boolean {
  return injectDeckLayerIds.has(layerId);
}

export function setOverlayLayerVisibility(
  _layerId: string,
  _visible: boolean
): void {
  // TODO: update layer visibility in inject deck when needed
}
